import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { ApplicationDataService } from '../../../core/services/application-data.service';

interface AiAgentSession {
  sr?: number;
  phoneNumber: string;
  messageCount: number;
  audioCount?: number;
  lastMessageTime?: string;
}

interface AiAgentMessage {
  id: number;
  message: string;
  dateTime: string;
  direction: 'INBOUND' | 'OUTBOUND';
  messageType?: string;
  status?: string;
  audioUrl?: string;
  audioObjectUrl?: string;
  transcriptionElapsedMs?: number;
  hermesElapsedMs?: number;
  ttsElapsedMs?: number;
  errorMessage?: string;
}

@Component({
  selector: 'app-whatsapp-ai-agent-conversations',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatInputModule, MatDatepickerModule, MatNativeDateModule],
  templateUrl: './ai-agent-conversations.component.html',
  styleUrls: ['./ai-agent-conversations.component.css']
})
export class WhatsAppAiAgentConversationsComponent implements OnInit, OnDestroy {
  sessions: AiAgentSession[] = [];
  totalElements = 0;
  totalPages = 0;
  currentPage = 0;
  pageSize = 20;
  searchControl = new FormControl('');
  fromDate = '';
  toDate = '';
  isLoading = false;

  showModal = false;
  isModalLoading = false;
  selectedPhone = '';
  messages: AiAgentMessage[] = [];

  private objectUrls: string[] = [];

  constructor(private appDataService: ApplicationDataService) {}

  ngOnInit(): void {
    this.loadSessions();
  }

  onSearchKeyPress(event: KeyboardEvent): boolean {
    return /\d/.test(event.key);
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');
    if (input.value !== digits) {
      this.searchControl.setValue(digits, { emitEvent: false });
      input.value = digits;
    }
  }

  applyFilters(): void {
    this.currentPage = 0;
    this.loadSessions();
  }

  onClear(): void {
    this.searchControl.setValue('');
    this.fromDate = '';
    this.toDate = '';
    this.currentPage = 0;
    this.loadSessions();
  }

  loadSessions(): void {
    this.isLoading = true;
    const body: any = {};
    if (this.searchControl.value) body.mobileNumber = this.searchControl.value;
    if (this.fromDate) body.dateFrom = new Date(this.fromDate).toISOString().split('T')[0] + 'T00:00:00';
    if (this.toDate) body.dateTo = new Date(this.toDate).toISOString().split('T')[0] + 'T23:59:59';

    this.appDataService.getWhatsAppAiAgentSessions(body, this.currentPage, this.pageSize).subscribe({
      next: (res) => {
        const list = Array.isArray(res?.content) ? res.content : [];
        this.sessions = list.map((s: any, i: number) => ({
          ...s,
          sr: this.currentPage * this.pageSize + i + 1
        }));
        this.totalElements = res?.totalElements ?? 0;
        this.totalPages = res?.totalPages ?? 0;
        this.isLoading = false;
      },
      error: () => {
        this.sessions = [];
        this.totalElements = 0;
        this.totalPages = 0;
        this.isLoading = false;
      }
    });
  }

  openConversation(phone: string): void {
    if (!phone) return;
    this.selectedPhone = phone;
    this.showModal = true;
    this.isModalLoading = true;
    this.releaseAudioUrls();

    this.appDataService.getWhatsAppAiAgentConversation(phone).subscribe({
      next: (res) => {
        this.messages = Array.isArray(res) ? res : res?.data || [];
        this.isModalLoading = false;
        this.prepareAudioMessages();
      },
      error: () => {
        this.messages = [];
        this.isModalLoading = false;
      }
    });
  }

  closeModal(): void {
    this.showModal = false;
    this.selectedPhone = '';
    this.messages = [];
    this.releaseAudioUrls();
  }

  prepareAudioMessages(): void {
    this.messages
      .filter(msg => this.hasAudio(msg))
      .forEach(msg => {
        const side = msg.direction === 'INBOUND' ? 'inbound' : 'reply';
        this.appDataService.getWhatsAppAiAgentAudio(msg.id, side).subscribe({
          next: (blob) => {
            const url = URL.createObjectURL(blob);
            this.objectUrls.push(url);
            msg.audioObjectUrl = url;
          },
          error: () => {
            msg.audioObjectUrl = '';
          }
        });
      });
  }

  hasAudio(msg: AiAgentMessage): boolean {
    return (msg.messageType || '').toLowerCase() === 'audio' && !!msg.audioUrl;
  }

  goToPrev(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.loadSessions();
    }
  }

  goToNext(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.loadSessions();
    }
  }

  get displayPage(): number { return this.currentPage + 1; }
  get pageEnd(): number { return Math.min((this.currentPage + 1) * this.pageSize, this.totalElements); }
  trackByIndex(index: number): number { return index; }

  private releaseAudioUrls(): void {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    this.objectUrls = [];
  }

  ngOnDestroy(): void {
    this.releaseAudioUrls();
  }
}
